import { useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import Highcharts from 'highcharts';

import ChartColorsContext from 'contexts/ChartColorsContext';
import useMetricMatrix from 'hooks/useMetricMatrix';
import getGroupByField from 'hooks/useMetricMatrix/getGroupByField';
import useChartDrillDowns from 'hooks/useChartDrillDowns';
import useValueFormatters from 'hooks/useValueFormatters';
import stackedArea from '../highchartOptions/stackedAreaOptions';
import {
  buildCategories,
  buildCategoriesAlt,
  buildStackedSeries,
  buildStackedSeriesAlt,
} from '../stackedFormatters';
import toAutoInterval from '../toAutoInterval';
import formatDateLabel from '../formatDateLabel';

const useStackedAreaData = ({
  chartDefinition,
  chartRef,
}: {
  chartDefinition: V5ChartDefinition;
  chartRef: React.MutableRefObject<any>;
}): {
  options: Highcharts.Options;
  groupKey: string;
} => {
  const { isLoading, rowData } = useMetricMatrix({
    chartDef: chartDefinition,
    skip2DRowDataFormatting: true,
  });
  const { getChartColor, getChartColorIndex } = useContext(ChartColorsContext);
  const [series, setSeries] = useState<StackedSeries[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [autoInterval, setAutoInterval] = useState<AutoInterval | undefined>();
  const { formatMetric } = useValueFormatters();

  const groupKey = getGroupByField(chartDefinition);

  const dateKey = (() => {
    if (
      chartDefinition.trendByCalendarInterval &&
      chartDefinition.trendByCalendarInterval !== 'auto'
    ) {
      return chartDefinition.trendByCalendarInterval;
    }
    return 'date';
  })();

  const { onDrillDown } = useChartDrillDowns(
    dateKey,
    chartRef,
    'stackedArea',
    chartDefinition,
  );

  // All hooks called above — safe to throw now
  if (groupKey === undefined) {
    throw new Error(
      `Group key not found for chartDefId: ${chartDefinition.id}`,
    );
  }

  useEffect(() => {
    if (!rowData) {
      return;
    }
    if (chartDefinition.trendByCalendarInterval !== 'auto') {
      return;
    }
    try {
      // @ts-ignore
      const aI = Object.keys(rowData[0])['interval'] as string;
      const newAI = toAutoInterval(aI);
      setAutoInterval((currentAi) =>
        _.isEqual(currentAi, newAI) ? currentAi : newAI,
      );
    } catch (ex) {
      return undefined;
    }
  }, [chartDefinition.trendByCalendarInterval, rowData]);

  const altPivot = chartDefinition.stackedAreaOptions
    ? chartDefinition.stackedAreaOptions.altPivot
    : false;

  const interval = (() => {
    if (
      chartDefinition.trendByCalendarInterval &&
      chartDefinition.trendByCalendarInterval !== 'auto'
    ) {
      return chartDefinition.trendByCalendarInterval;
    }
    return undefined;
  })();

  const options = stackedArea({
    title: ' ',
    formatMetric,
    series: series.map((s) => ({ ...s, onDrillDown, interval })),
    xAxis: {
      categories,
      labels: {
        // @ts-ignore
        formatter: function () {
          if (
            chartDefinition.trendByCalendarInterval &&
            chartDefinition.trendByCalendarInterval !== 'auto'
          ) {
            // @ts-ignore
            return formatDateLabel(
              // @ts-ignore
              this.value,
              chartDefinition.trendByCalendarInterval,
            );
          }
          // @ts-ignore
          return this.value;
        },
      },
    },
  });

  useEffect(() => {
    if (!isLoading) {
      if (altPivot) {
        setSeries(
          buildStackedSeriesAlt({
            rowData,
            groupKey,
            chartDef: chartDefinition,
            autoInterval,
            getChartColor,
            getChartColorIndex,
          }),
        );
        setCategories(buildCategoriesAlt(rowData, groupKey));
      } else {
        const { prettyDates: newCategories, termLookup } = buildCategories(
          rowData,
          groupKey,
          chartDefinition,
          autoInterval,
        );
        setSeries(
          buildStackedSeries({
            rowData,
            groupKey,
            newCategories,
            newTermLookup: termLookup,
            getChartColor,
            getChartColorIndex,
          }),
        );
        setCategories(newCategories);
      }
    }
  }, [
    groupKey,
    rowData,
    isLoading,
    altPivot,
    chartDefinition,
    autoInterval,
    getChartColor,
    getChartColorIndex,
  ]);

  return { options: options as any as Highcharts.Options, groupKey };
};

export default useStackedAreaData;
